import { useState } from 'react'
import { useWizardStore } from '../../store/wizardStore'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import { api } from '../../lib/api'

export default function Step6SantaDetails() {
  const { nextStep, previousStep, setSantaVisit, santaVisit, selectedTimeSlotId, orderId, setWantsSanta, setSlotHeldUntil, expireSlot } = useWizardStore()
  const [childrenNames, setChildrenNames] = useState<string[]>(
    santaVisit?.childrenNames || ['']
  )
  const [hasAllergies, setHasAllergies] = useState(
    santaVisit?.dietaryRequirements ? santaVisit.dietaryRequirements.length > 0 : false
  )
  const [dietaryRequirements, setDietaryRequirements] = useState(
    santaVisit?.dietaryRequirements || ''
  )
  const [errors, setErrors] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const handleAddChild = () => {
    if (childrenNames.length < 5) {
      setChildrenNames([...childrenNames, ''])
    }
  }

  const handleRemoveChild = (index: number) => {
    setChildrenNames(childrenNames.filter((_, i) => i !== index))
  }

  const handleChildNameChange = (index: number, value: string) => {
    const newNames = [...childrenNames]
    newNames[index] = value
    setChildrenNames(newNames)
  }

  const handleNext = async () => {
    const newErrors: string[] = []

    childrenNames.forEach((name, index) => {
      if (!name.trim()) {
        newErrors[index] = 'Please enter the child\'s name'
      }
    })

    setErrors(newErrors)

    if (newErrors.filter(Boolean).length === 0 && orderId && selectedTimeSlotId) {
      setIsLoading(true)
      try {
        // Only include dietary requirements if checkbox is checked
        const finalDietaryRequirements = hasAllergies ? dietaryRequirements : ''

        const booking = await api.createBooking({
          orderId,
          timeSlotId: selectedTimeSlotId,
          childrenNames: childrenNames,
          dietaryRequirements: finalDietaryRequirements,
        })
        setSlotHeldUntil(booking.lockedUntil)

        // Store in wizard state
        setSantaVisit({
          timeSlotId: selectedTimeSlotId,
          childrenNames: childrenNames,
          dietaryRequirements: finalDietaryRequirements,
        })

        // Mark that user wants Santa visit
        setWantsSanta(true)

        nextStep()
      } catch (error: any) {
        console.error('Failed to create booking:', error)
        if (error.status === 409) {
          expireSlot() // slot lost: back to the slot picker with a notice
          return
        }
        setErrors(['Failed to create booking. Please try again.'])
      } finally {
        setIsLoading(false)
      }
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Santa Visit Details</CardTitle>
        <p className="text-gray-600 mt-2">
          Tell us about the children visiting Santa and any dietary requirements.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div>
            <h3 className="font-semibold text-gray-900 mb-4">Children Visiting Santa</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please enter the name(s) of the children who will be visiting Santa.
            </p>
            <div className="space-y-3">
              {childrenNames.map((name, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1">
                    <Input
                      label={`Child ${index + 1} Name`}
                      type="text"
                      placeholder="Enter child's name"
                      value={name}
                      onChange={(e) => handleChildNameChange(index, e.target.value)}
                      error={errors[index]}
                    />
                  </div>
                  {childrenNames.length > 1 && (
                    <button
                      onClick={() => handleRemoveChild(index)}
                      className="text-primary-800 hover:bg-primary-50 rounded-lg p-2.5 mt-8"
                      aria-label={`Remove child ${index + 1}`}
                    >
                      <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}

              {childrenNames.length < 5 && (
                <Button
                  onClick={handleAddChild}
                  fullWidth
                  variant="outline"
                >
                  + Add Another Child
                </Button>
              )}
            </div>
          </div>

          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={hasAllergies}
                onChange={(e) => {
                  setHasAllergies(e.target.checked)
                  if (!e.target.checked) {
                    setDietaryRequirements('')
                  }
                }}
                className="w-5 h-5 rounded border-2 border-gray-300 text-accent-800 focus:ring-accent-800 focus:ring-2"
              />
              <span className="text-sm font-medium text-gray-700">
                My child has allergies or dietary requirements
              </span>
            </label>
            {hasAllergies && (
              <div className="mt-3">
                <label htmlFor="dietary-requirements" className="block text-sm font-medium text-gray-700 mb-2">
                  Please describe the allergies or dietary requirements
                </label>
                <textarea
                  id="dietary-requirements"
                  className="w-full px-4 py-3 text-base rounded-lg border-2 border-gray-300 focus:border-accent-800 focus:ring-2 focus:ring-accent-300 focus:outline-none transition-colors resize-none"
                  rows={3}
                  placeholder="Please describe the allergies or dietary requirements..."
                  value={dietaryRequirements}
                  onChange={(e) => setDietaryRequirements(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="pt-4 space-y-2">
            <Button
              onClick={handleNext}
              fullWidth
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Booking...' : 'Continue to Payment'}
            </Button>
            <Button
              onClick={previousStep}
              fullWidth
              variant="ghost"
              disabled={isLoading}
            >
              Back
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  )
}
